// =============================================
// Admin Organizations Management Page
// Kipikid - ניהול ארגונים
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  User,
  X,
  ChevronLeft,
  Edit2,
  AlertCircle,
  Loader2,
  Baby,
} from 'lucide-react';
import { SuccessToast } from '../../components/shared/SuccessToast';
import { useSuccessToast } from '../../hooks/useSuccessToast';
import {
  adminApi,
  type OrganizationDto,
  type CreateOrganizationRequest,
  type UpdateOrganizationRequest,
  type CreateOrganizationManagerRequest,
} from '../../services/api';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminOrgHelpTexts, adminManagerHelpTexts } from '../../constants/helpTexts';
import { adminColors as colors } from '../../styles/colors';

const orgTypeOptions = [
  { value: 1, label: 'רשת' },
  { value: 2, label: 'עירייה' },
  { value: 3, label: 'ממשלתי' },
];

export default function AdminOrganizations() {
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);
  const [showManagerModal, setShowManagerModal] = useState<OrganizationDto | null>(null);
  const { message: successMessage, show: showSuccess } = useSuccessToast();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const result = await adminApi.getOrganizations();
        if (result.success && result.data) {
          setOrganizations(result.data);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrganizations();
  }, [refreshKey]);

  const filteredOrgs = organizations.filter(org =>
    org.name.includes(searchQuery) ||
    org.organizationTypeName.includes(searchQuery) ||
    org.contactName?.includes(searchQuery) ||
    org.contactPhone?.includes(searchQuery)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            ניהול ארגונים
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {organizations.length} ארגונים במערכת
          </p>
        </div>
        <motion.button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            backgroundColor: colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          ארגון חדש
        </motion.button>
      </div>

      {/* Success Message */}
      <SuccessToast message={successMessage} color={colors.success} />

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש לפי שם, סוג או איש קשר..."
          style={{
            width: '100%',
            padding: '14px 44px 14px 14px',
            fontSize: '15px',
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
            direction: 'rtl',
          }}
        />
        <Search
          size={20}
          color={colors.textLight}
          style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>

      {/* Organizations List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען ארגונים...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredOrgs.map((org, index) => (
            <motion.div
              key={org.publicId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedOrg(org)}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                backgroundColor: org.isActive ? colors.primaryLight : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Building2 size={24} color={org.isActive ? colors.primary : colors.textLight} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                    {org.name}
                  </h3>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: colors.secondaryLight,
                    color: colors.secondary,
                  }}>
                    {org.organizationTypeName}
                  </span>
                  {!org.isActive && (
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      backgroundColor: '#fee2e2',
                      color: colors.error,
                    }}>
                      לא פעיל
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Baby size={14} />
                    {org.kindergartenCount} גנים
                  </span>
                  {org.contactName && (
                    <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <User size={14} />
                      {org.contactName}
                    </span>
                  )}
                  {org.contactPhone && (
                    <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Phone size={14} />
                      {org.contactPhone}
                    </span>
                  )}
                </div>
              </div>

              <ChevronLeft size={20} color={colors.textLight} />
            </motion.div>
          ))}
          {filteredOrgs.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: 'white',
              borderRadius: '16px',
            }}>
              <Building2 size={48} color={colors.textLight} style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', color: colors.text, fontWeight: '500' }}>
                {searchQuery ? 'לא נמצאו ארגונים' : 'אין ארגונים עדיין'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add Organization Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddOrganizationModal
            onClose={() => setShowAddModal(false)}
            onCreated={(newOrg) => {
              setOrganizations(prev => [...prev, newOrg]);
              setShowAddModal(false);
              showSuccess(`ארגון "${newOrg.name}" נוצר בהצלחה`);
            }}
          />
        )}
      </AnimatePresence>

      {/* Organization Details Modal */}
      <AnimatePresence>
        {selectedOrg && (
          <OrganizationDetailsModal
            organization={selectedOrg}
            onClose={() => setSelectedOrg(null)}
            onUpdated={(updated) => {
              setOrganizations(prev => prev.map(o => o.publicId === updated.publicId ? updated : o));
              setSelectedOrg(updated);
              showSuccess('הארגון עודכן בהצלחה');
            }}
            onAddManager={() => {
              const org = selectedOrg;
              setSelectedOrg(null);
              setShowManagerModal(org);
            }}
          />
        )}
      </AnimatePresence>

      {/* Add Manager Modal */}
      <AnimatePresence>
        {showManagerModal && (
          <AddManagerModal
            organization={showManagerModal}
            onClose={() => setShowManagerModal(null)}
            onCreated={() => {
              setShowManagerModal(null);
              showSuccess('מנהל ארגון נוצר בהצלחה');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// Organization Details Modal
// ==========================================

function OrganizationDetailsModal({
  organization,
  onClose,
  onUpdated,
  onAddManager,
}: {
  organization: OrganizationDto;
  onClose: () => void;
  onUpdated: (updated: OrganizationDto) => void;
  onAddManager: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(organization.name);
  const [editContactName, setEditContactName] = useState(organization.contactName || '');
  const [editContactPhone, setEditContactPhone] = useState(organization.contactPhone || '');
  const [editContactEmail, setEditContactEmail] = useState(organization.contactEmail || '');
  const [editIsActive, setEditIsActive] = useState(organization.isActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!editName.trim()) {
      setError('שם ארגון הוא שדה חובה');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data: UpdateOrganizationRequest = {
        name: editName.trim(),
        contactName: editContactName.trim() || undefined,
        contactPhone: editContactPhone.trim() || undefined,
        contactEmail: editContactEmail.trim() || undefined,
        isActive: editIsActive,
      };
      const result = await adminApi.updateOrganization(organization.publicId, data);
      if (result.success && result.data) {
        onUpdated(result.data);
        setIsEditing(false);
      } else {
        setError(result.message || 'שגיאה בעדכון');
      }
    } catch {
      setError('שגיאה בעדכון הארגון');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '440px',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
              {organization.name}
            </h2>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: colors.secondaryLight,
                color: colors.secondary,
              }}>
                {organization.organizationTypeName}
              </span>
              {!organization.isActive && (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: '#fee2e2',
                  color: colors.error,
                }}>
                  לא פעיל
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: colors.background,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color={colors.textLight} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {isEditing ? (
          /* Edit Mode */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <div>
              <FormLabel text="שם ארגון" required helpText={adminOrgHelpTexts.name.tooltip} style={{ fontSize: '12px', color: colors.textLight }} />
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <FormLabel text="איש קשר" helpText={adminOrgHelpTexts.contactName.tooltip} style={{ fontSize: '12px', color: colors.textLight }} />
              <input type="text" value={editContactName} onChange={(e) => setEditContactName(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <FormLabel text="טלפון" helpText={adminOrgHelpTexts.contactPhone.tooltip} style={{ fontSize: '12px', color: colors.textLight }} />
                <input type="tel" value={editContactPhone} onChange={(e) => setEditContactPhone(e.target.value)} style={inputStyle} />
                <HelperText text={adminOrgHelpTexts.contactPhone.helper!} />
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel text="אימייל" helpText={adminOrgHelpTexts.contactEmail.tooltip} style={{ fontSize: '12px', color: colors.textLight }} />
                <input type="email" value={editContactEmail} onChange={(e) => setEditContactEmail(e.target.value)} style={{ ...inputStyle, direction: 'ltr' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FormLabel text="ארגון פעיל" helpText={adminOrgHelpTexts.isActive.tooltip} style={{ fontSize: '13px', color: colors.text, marginBottom: 0 }} />
              <button
                onClick={() => setEditIsActive(!editIsActive)}
                style={{
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: editIsActive ? colors.success : colors.border,
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: editIsActive ? '2px' : '22px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <motion.button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: colors.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                whileTap={{ scale: saving ? 1 : 0.98 }}
              >
                {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {saving ? 'שומר...' : 'שמור'}
              </motion.button>
              <motion.button
                onClick={() => setIsEditing(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: 'white',
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                ביטול
              </motion.button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <DetailRow icon={Baby} label="גנים" value={`${organization.kindergartenCount} גנים`} />
              {organization.contactName && <DetailRow icon={User} label="איש קשר" value={organization.contactName} />}
              {organization.contactPhone && <DetailRow icon={Phone} label="טלפון" value={organization.contactPhone} />}
              {organization.contactEmail && <DetailRow icon={Mail} label="אימייל" value={organization.contactEmail} />}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <motion.button
                onClick={() => setIsEditing(true)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '12px',
                  backgroundColor: colors.primaryLight,
                  color: colors.primary,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <Edit2 size={16} />
                עריכה
              </motion.button>
              <motion.button
                onClick={onAddManager}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '12px',
                  backgroundColor: colors.secondaryLight,
                  color: colors.secondary,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <User size={16} />
                מנהל
              </motion.button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: colors.background, borderRadius: '10px' }}>
      <Icon size={18} color={colors.primary} />
      <div>
        <p style={{ fontSize: '11px', color: colors.textLight }}>{label}</p>
        <p style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>{value}</p>
      </div>
    </div>
  );
}

// ==========================================
// Add Organization Modal
// ==========================================

function AddOrganizationModal({ onClose, onCreated }: { onClose: () => void; onCreated: (org: OrganizationDto) => void }) {
  const [name, setName] = useState('');
  const [organizationType, setOrganizationType] = useState(1);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('שם ארגון הוא שדה חובה');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const request: CreateOrganizationRequest = {
        name: name.trim(),
        organizationType,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      };

      const result = await adminApi.createOrganization(request);
      if (result.success && result.data) {
        onCreated(result.data);
      } else {
        setError(result.message || 'שגיאה ביצירת ארגון');
      }
    } catch {
      setError('שגיאה ביצירת ארגון');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '6px',
    display: 'block',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '440px',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>ארגון חדש</h2>
          <button
            onClick={onClose}
            style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: colors.background, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={18} color={colors.textLight} />
          </button>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#fef2f2', borderRadius: '8px', marginBottom: '16px' }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <FormLabel text="שם ארגון" required helpText={adminOrgHelpTexts.name.tooltip} style={{ color: colors.text }} />
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="שם הארגון" style={inputStyle} />
          </div>

          <div>
            <FormLabel text="סוג ארגון" required helpText={adminOrgHelpTexts.organizationType.tooltip} style={{ color: colors.text }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              {orgTypeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setOrganizationType(opt.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: `2px solid ${organizationType === opt.value ? colors.primary : colors.border}`,
                    backgroundColor: organizationType === opt.value ? colors.primaryLight : 'white',
                    color: organizationType === opt.value ? colors.primary : colors.text,
                    fontSize: '14px',
                    fontWeight: organizationType === opt.value ? '600' : '400',
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <FormLabel text="איש קשר" helpText={adminOrgHelpTexts.contactName.tooltip} style={{ color: colors.text }} />
            <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="שם איש קשר" style={inputStyle} />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <FormLabel text="טלפון" helpText={adminOrgHelpTexts.contactPhone.tooltip} style={{ color: colors.text }} />
              <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="050-000-0000" style={inputStyle} />
              <HelperText text={adminOrgHelpTexts.contactPhone.helper!} />
            </div>
            <div style={{ flex: 1 }}>
              <FormLabel text="אימייל" helpText={adminOrgHelpTexts.contactEmail.tooltip} style={{ color: colors.text }} />
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} style={{ ...inputStyle, direction: 'ltr' }} />
            </div>
          </div>
        </div>

        <motion.button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: '100%',
            marginTop: '24px',
            padding: '14px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #4f46e5 100%)`,
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        >
          {isSubmitting ? 'יוצר...' : 'צור ארגון'}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// ==========================================
// Add Manager Modal
// ==========================================

function AddManagerModal({
  organization,
  onClose,
  onCreated,
}: {
  organization: OrganizationDto;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !username.trim() || !password.trim()) {
      setError('יש למלא את כל שדות החובה');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const request: CreateOrganizationManagerRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        username: username.trim(),
        password: password.trim(),
      };

      const result = await adminApi.createOrganizationManager(organization.publicId, request);
      if (result.success) {
        onCreated();
      } else {
        setError(result.message || 'שגיאה ביצירת מנהל');
      }
    } catch {
      setError('שגיאה ביצירת מנהל');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '6px',
    display: 'block',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '440px',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>מנהל ארגון חדש</h2>
            <p style={{ fontSize: '13px', color: colors.textLight }}>{organization.name}</p>
          </div>
          <button
            onClick={onClose}
            style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: colors.background, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={18} color={colors.textLight} />
          </button>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#fef2f2', borderRadius: '8px', marginBottom: '16px' }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <FormLabel text="שם פרטי" required helpText={adminManagerHelpTexts.firstName.tooltip} style={{ color: colors.text }} />
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <FormLabel text="שם משפחה" required helpText={adminManagerHelpTexts.lastName.tooltip} style={{ color: colors.text }} />
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div>
            <FormLabel text="טלפון" required helpText={adminManagerHelpTexts.phone.tooltip} style={{ color: colors.text }} />
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="050-000-0000" style={inputStyle} />
            <HelperText text={adminManagerHelpTexts.phone.helper!} />
          </div>

          <div>
            <FormLabel text="אימייל" helpText={adminManagerHelpTexts.email.tooltip} style={{ color: colors.text }} />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, direction: 'ltr' }} />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <FormLabel text="שם משתמש" required helpText={adminManagerHelpTexts.username.tooltip} style={{ color: colors.text }} />
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={{ ...inputStyle, direction: 'ltr' }} />
              <HelperText text={adminManagerHelpTexts.username.helper!} />
            </div>
            <div style={{ flex: 1 }}>
              <FormLabel text="סיסמה" required helpText={adminManagerHelpTexts.password.tooltip} style={{ color: colors.text }} />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, direction: 'ltr' }} />
              <HelperText text={adminManagerHelpTexts.password.helper!} />
            </div>
          </div>
        </div>

        <motion.button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: '100%',
            marginTop: '24px',
            padding: '14px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${colors.secondary} 0%, #0d9488 100%)`,
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        >
          {isSubmitting ? 'יוצר...' : 'צור מנהל'}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
